import { api } from './api';
import { toNumber, toStr } from './transform';
import type {
  DailyPerformance,
  PerformanceMetrics,
  PortfolioHistory,
  PortfolioSummary,
  Position,
} from '@/types';

export interface PerformanceData {
  metrics: PerformanceMetrics;
  history: DailyPerformance[];
}

class PortfolioService {
  /**
   * Get portfolio summary including total value, cash, positions, and P&L.
   * Backend returns snake_case with Decimal-as-string values.
   * Transform to match frontend PortfolioSummary type.
   */
  async getPortfolioSummary(): Promise<PortfolioSummary> {
    const res = await api.get<Record<string, unknown>>('/portfolio/summary');
    return {
      total_value: toNumber(res.total_value),
      cash_balance: toNumber(res.cash),
      positions_value: toNumber(res.positions_value),
      total_pnl: toNumber(res.total_pnl),
      total_pnl_percent: toNumber(res.total_pnl_pct),
      day_pnl: toNumber(res.daily_pnl),
      day_pnl_percent: toNumber(res.daily_pnl_pct),
      updated_at: toStr(res.last_updated, new Date().toISOString()),
    };
  }

  /**
   * Get all current positions.
   * Backend returns snake_case with Decimal-as-string values.
   * Transform to match frontend Position type.
   */
  async getPositions(): Promise<Position[]> {
    const positions = await api.get<Array<Record<string, unknown>>>('/portfolio/positions');
    if (!Array.isArray(positions)) return [];
    return positions.map(mapPositionFromApi);
  }

  /**
   * Get a specific position by symbol
   */
  async getPosition(symbol: string): Promise<Position> {
    const p = await api.get<Record<string, unknown>>(`/portfolio/positions/${symbol}`);
    return mapPositionFromApi(p);
  }

  /**
   * Get portfolio history for charting.
   * Backend returns { period, data_points: [{timestamp, value}] }
   * Transform to PortfolioHistory[] for the frontend.
   */
  async getHistory(period: string = '1M'): Promise<PortfolioHistory[]> {
    const res = await api.get<Record<string, unknown>>(`/portfolio/history?period=${period}`);
    const dataPoints = (res.data_points as Array<Record<string, unknown>>) || [];
    return dataPoints.map((point) => ({
      timestamp: String(point.timestamp ?? ''),
      total_value: Number(point.value ?? 0),
      cash_balance: Number(point.cash_balance ?? 0),
      positions_value: Number(point.positions_value ?? 0),
    }));
  }

  /**
   * Get performance data including metrics and daily history
   */
  async getPerformance(period: string = '1M'): Promise<PerformanceData> {
    return api.get<PerformanceData>(`/portfolio/performance?period=${period}`);
  }

  /**
   * Get performance metrics only
   */
  async getPerformanceMetrics(): Promise<PerformanceMetrics> {
    return api.get<PerformanceMetrics>('/portfolio/performance/metrics');
  }

  /**
   * Get daily performance data
   */
  async getDailyPerformance(fromDate?: string, toDate?: string): Promise<DailyPerformance[]> {
    const params = new URLSearchParams();
    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);
    const query = params.toString();
    return api.get<DailyPerformance[]>(`/portfolio/performance/daily${query ? `?${query}` : ''}`);
  }
}

/** Map a single position from API snake_case to frontend Position type. */
function mapPositionFromApi(p: Record<string, unknown>): Position {
  return {
    id: toStr(p.id),
    user_id: '',
    symbol: toStr(p.symbol),
    quantity: toNumber(p.quantity),
    avg_entry_price: toNumber(p.average_cost),
    current_price: toNumber(p.current_price),
    market_value: toNumber(p.current_value),
    cost_basis: toNumber(p.quantity) * toNumber(p.average_cost),
    unrealized_pnl: toNumber(p.unrealized_pl),
    unrealized_pnl_percent: toNumber(p.unrealized_pl_pct),
    realized_pnl: toNumber(p.realized_pnl),
    side: String(p.side ?? 'long') as 'long' | 'short',
    opened_at: toStr(p.created_at),
    updated_at: toStr(p.updated_at),
  };
}

export const portfolioService = new PortfolioService();
